<template>
  <v-container fluid grid-list-sm>
    <v-card>
      <v-toolbar flat>
        <v-toolbar-title>
          <span class="font-weight-medium">
            {{ $t('ITEM.EDIT') }}
            {{ $t('LISTS.ITEM') }}
          </span>
        </v-toolbar-title>

        <v-spacer />

        <v-btn dark color="indigo" @click="$router.go(-1)">
          {{ $t('BUTTON.BACK') }}
        </v-btn>
      </v-toolbar>
      <v-divider />
    </v-card>
    <v-card :loading="loading && !form.id" class="mt-3">
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <documents-lists-form
              v-model="form"
              :errors="errors.getAll()"
              @input="updateState"
              @valid="valid = $event"
              @update-prop="clearErrorByName($event)"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-btn
              dark
              class="ml-2 float-right"
              color="primary"
              @click="createItem"
            >
              <v-icon left>mdi-plus</v-icon>
              {{ $t('ITEM.ADD') }} {{ $t('ITEM.ITEM') }}
            </v-btn>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <p class="text-subtitle-1">{{ $t('TILES.STRUCTURE_TITLE') }}:</p>
            <nested-draggable
              v-model="form.items"
              @end="updateState"
              @edit-tile="edit"
              @remove-tile="removeConfirm"
            >
              <template #parent-title="{ item }">
                <span>{{ item.title }}</span>
                <v-spacer></v-spacer>
                <span class="grey--text mr-8">{{ item.type.name }}</span>
              </template>
              <template #title="{ item }">
                <span>{{ item.title }}</span>
                <v-spacer></v-spacer>
                <span class="grey--text mr-12">{{ item.type.name }}</span>
              </template>
            </nested-draggable>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-spacer></v-spacer>
            <v-btn
              class="float-right"
              color="success"
              large
              depressed
              :disabled="!valid"
              @click="confirmUpdate"
            >
              {{ $t('BUTTON.SAVE') }}
            </v-btn>
          </v-col>
        </v-row>

        <v-dialog v-model="dialog" persistent max-width="800px">
          <template v-if="action">
            <component
              :is="`item-${action}`"
              :id="currentId"
              :key="currentId"
              :list-id="id"
              @created="created"
              @edited="edited"
              @cancel="cancel"
            ></component>
          </template>
        </v-dialog>
        <confirm-dialog ref="confirm"></confirm-dialog>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import DocumentsListsForm from '@/modules/documents_lists/components/forms/DocumentsListsForm';
import ItemCreate from '@/modules/documents_lists/components/forms/ItemCreate';
import ItemEdit from '@/modules/documents_lists/components/forms/ItemEdit';
import errors from '@/modules/_global/mixins/errors';
import { DocumentsListsService } from '@/services/documents-lists.service';
import i18nRefreshData from '@/modules/_global/mixins/i18nRefreshData';
import { mapActions, mapState } from 'vuex';
import { DocumentsListsItemsService } from '@/services/documents-lists-items.service';

export default {
  name: 'DocumentsListsShowPage',

  components: {
    DocumentsListsForm,
    ItemCreate,
    ItemEdit,
    NestedDraggable: () =>
      import('@/modules/_global/_components/NestedDraggable'),
    ConfirmDialog: () => import('@/modules/_global/_components/ConfirmDialog')
  },

  mixins: [errors, i18nRefreshData],

  props: {
    id: {
      type: Number,
      required: true
    }
  },

  data() {
    return {
      form: {
        items: []
      },
      unwatch: null,
      valid: true,
      action: null,
      dialog: false,
      currentId: null
    };
  },

  computed: {
    ...mapState(['loading'])
  },

  mounted() {
    this.refreshData();
  },

  methods: {
    ...mapActions('snackbar', ['setSnack']),

    refreshData() {
      DocumentsListsService.getById(this.id).then(
        data => (this.form = data.data)
      );
    },

    createItem() {
      this.action = 'create';
      this.dialog = true;
    },

    updateState() {},

    created() {
      this.refreshData();
      this.cancel();
    },

    edited(item) {
      this.form.items = this.replaceItem(this.form.items, item);
      this.cancel();
    },

    edit(item) {
      this.currentId = item.id;
      this.action = 'edit';
      this.dialog = true;
    },

    removeItem(arr, match) {
      return arr
        .map(el =>
          el.children
            ? { ...el, children: this.removeItem(el.children, match) }
            : el
        )
        .filter(el => (el.children && el.children.length) || match !== el.id);
    },

    replaceItem(arr, item) {
      return arr.map(el =>
        el.id === item.id
          ? { ...item, children: el.children }
          : el.children
          ? { ...el, children: this.replaceItem(el.children, item.id) }
          : el
      );
    },

    removeConfirm(item) {
      this.$refs.confirm
        .open(`${this.$t('ITEM.DELETE')} ${this.$t('ITEM.ITEM')}?`)
        .then(confirm => {
          if (confirm) {
            DocumentsListsItemsService.remove(this.id, item.id)
              .then(({ data }) => {
                this.setSnack({
                  status: 'success',
                  text: this.$t('MESSAGE.SUCCESS')
                });
                this.form.items = this.removeItem(this.form.items, item.id);
              })
              .catch(console.error);
          }
        });
    },

    confirmUpdate() {
      DocumentsListsService.update(this.id, this.form)
        .then(({ data }) => {
          this.setSnack({
            status: 'success',
            text: this.$t('MESSAGE.SUCCESS')
          });

          this.$router.push({ name: 'documents-lists' });
        })
        .catch(this.handleErrors);
    },

    cancel() {
      this.dialog = false;
      this.action = null;
    }
  }
};
</script>

<style scoped></style>
